import { useState, useEffect } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { getRiskSettings, saveRiskSettings } from "@/lib/delivery";
import { Loader2 } from "lucide-react";

// Simplified editor: exposes the raw JSON structure to the operator via labeled
// numeric inputs. Keeps every knob configurable without a heavy schema.
const WEIGHT_LABELS = {
  phone_refuse_1: "1 refuz anterior pe telefon",
  phone_delivered_bonus: "Bonus livrare reușită (client recurent, negativ)",
  phone_no_history: "Fără istoric pe telefon",
  new_customer: "Client fără livrări reușite (nou)",
  cod_high: "COD peste pragul „ridicat”",
  cod_medium: "COD peste pragul „mediu”",
  cod_low: "COD peste pragul „scăzut”",
  county_rate_medium: "Județ cu refuz > medie + delta mediu",
  county_rate_high: "Județ cu refuz > medie + delta ridicat",
};

export default function RiskSettingsDialog({ open, onOpenChange, onSaved }) {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    getRiskSettings()
      .then((s) => setSettings(s))
      .catch((e) => toast.error("Nu pot încărca setările: " + e.message))
      .finally(() => setLoading(false));
  }, [open]);

  const upd = (path, val) => {
    setSettings((s) => {
      const copy = JSON.parse(JSON.stringify(s || {}));
      const parts = path.split(".");
      let cur = copy;
      for (let i = 0; i < parts.length - 1; i++) {
        cur[parts[i]] = cur[parts[i]] || {};
        cur = cur[parts[i]];
      }
      cur[parts[parts.length - 1]] = val;
      return copy;
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      const patch = {
        weights: settings.weights,
        cod_thresholds: settings.cod_thresholds,
        county_deltas: settings.county_deltas,
        level_thresholds: settings.level_thresholds,
        backfill_days: settings.backfill_days,
      };
      await saveRiskSettings(patch);
      toast.success("Setările au fost salvate. Recalculează coada pentru a aplica.");
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Setări scoring risc COD</DialogTitle>
        </DialogHeader>
        {loading || !settings ? (
          <div className="py-10 flex items-center justify-center text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin mr-2" /> Se încarcă...
          </div>
        ) : (
          <div className="space-y-6 text-sm">
            <div>
              <div className="font-medium mb-2">Ponderi factori (0-100)</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {Object.entries(settings.weights || {}).map(([k, v]) => (
                  <div key={k} className="space-y-1">
                    <Label className="text-xs text-muted-foreground">{WEIGHT_LABELS[k] || k}</Label>
                    <Input
                      type="number"
                      value={v}
                      onChange={(e) => upd(`weights.${k}`, parseInt(e.target.value) || 0)}
                      className="h-8"
                      data-testid={`weight-${k}`}
                    />
                  </div>
                ))}
              </div>
            </div>

            <div>
              <div className="font-medium mb-2">Praguri valoare COD (lei)</div>
              <div className="grid grid-cols-3 gap-3">
                {["low", "medium", "high"].map((k) => (
                  <div key={k} className="space-y-1">
                    <Label className="text-xs text-muted-foreground">{k === "low" ? "Scăzut" : k === "medium" ? "Mediu" : "Ridicat"}</Label>
                    <Input
                      type="number"
                      value={settings.cod_thresholds?.[k] ?? 0}
                      onChange={(e) => upd(`cod_thresholds.${k}`, parseInt(e.target.value) || 0)}
                      className="h-8"
                      data-testid={`cod-threshold-${k}`}
                    />
                  </div>
                ))}
              </div>
            </div>

            <div>
              <div className="font-medium mb-2">Delta județ vs media națională (puncte procentuale)</div>
              <div className="grid grid-cols-2 gap-3">
                {["medium", "high"].map((k) => (
                  <div key={k} className="space-y-1">
                    <Label className="text-xs text-muted-foreground">Delta {k === "medium" ? "mediu" : "ridicat"}</Label>
                    <Input
                      type="number"
                      step="0.5"
                      value={settings.county_deltas?.[k] ?? 0}
                      onChange={(e) => upd(`county_deltas.${k}`, parseFloat(e.target.value) || 0)}
                      className="h-8"
                      data-testid={`county-delta-${k}`}
                    />
                  </div>
                ))}
              </div>
            </div>

            <div>
              <div className="font-medium mb-2">Praguri nivel de risc (scor)</div>
              <div className="grid grid-cols-2 gap-3">
                {["medium", "high"].map((k) => (
                  <div key={k} className="space-y-1">
                    <Label className="text-xs text-muted-foreground">
                      Scor minim pentru „{k === "medium" ? "Mediu" : "Ridicat"}”
                    </Label>
                    <Input
                      type="number"
                      value={settings.level_thresholds?.[k] ?? 0}
                      onChange={(e) => upd(`level_thresholds.${k}`, parseInt(e.target.value) || 0)}
                      className="h-8"
                      data-testid={`level-threshold-${k}`}
                    />
                  </div>
                ))}
              </div>
            </div>

            <div>
              <div className="font-medium mb-2">Fereastră backfill</div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Ultimele N zile</Label>
                  <Input
                    type="number"
                    value={settings.backfill_days ?? 30}
                    onChange={(e) => upd("backfill_days", parseInt(e.target.value) || 30)}
                    className="h-8"
                    data-testid="backfill-days-input"
                  />
                </div>
              </div>
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Anulează</Button>
          <Button onClick={save} disabled={saving || !settings} data-testid="save-risk-settings-btn">
            {saving && <Loader2 className="h-4 w-4 animate-spin mr-1.5" />}
            Salvează
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
